import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { Search, Settings, Pencil, MapPin, ChevronDown, ChevronRight, CheckCircle, XCircle, Edit, Trash2, UserPlus, Lock, Repeat } from "lucide-react";
import {
  fetchMemberApi,
  FetchLocationApi,
  updateMemberAPI,
  changeManagerAPI,
  inActiveUser,
  fetchUnassignedUsersAPI,
  assignMembersAPI,
  changeUserOrganization,
  fetchAccessibleOrgsAPI,
  fetchSubOrgDropdown,
} from "../../actioncreators/actioncreators";
import AddMember from "./AddMember";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import "./dashboard.css";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { IconButton } from "@mui/material";
import ArthaTextField from "../../fields/ArthaTextField";
import ArthaCancelButton from "../../fields/ArthaCancelButton";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { orgid } from "../../actions/loanAction";
import { useSuspension } from "../../contexts/SuspensionContext";
const UserAvatar = ({ member }) => {
  const [imageError, setImageError] = useState(false);

  const getInitials = (fullName) => {
    return fullName ? fullName[0].toUpperCase() : "";
  };

  if (!member.user_image || imageError) {
    return (
      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
        {getInitials(member.full_name)}
      </div>
    );
  }

  return (
    <img
      src={`data:image/jpeg;base64,${member.user_image}`}
      alt={member.full_name}
      className="w-8 h-8 rounded-full object-cover"
      onError={() => setImageError(true)}
    />
  );
};

const UserAvatar1 = ({ selectedMember }) => {
  const [imageError, setImageError] = useState(false);

  const getInitials = (fullName) => {
    return fullName ? fullName[0].toUpperCase() : "";
  };

  if (!selectedMember?.user_image || imageError) {
    return (
      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
        {getInitials(selectedMember?.full_name)}
      </div>
    );
  }

  return (
    <img
      src={`data:image/jpeg;base64,${selectedMember.user_image}`}
      alt={selectedMember.full_name}
      className="w-8 h-8 rounded-full object-cover"
      onError={() => setImageError(true)}
    />
  );
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Members = () => {
  const [members, setMembers] = useState([]);
  const isMountedRef = useRef(false); // Track if component is mounted
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 20;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState(""); // Filter by role

  // Check if organization is suspended
  const { isSuspended } = useSuspension();
  const [identificationFilter, setIdentificationFilter] = useState(""); // Filter by identification status
  const [statusFilter, setStatusFilter] = useState("");
  // Organization filter: "" = All (root + every sub-org mixed, existing
  // behaviour), "__ROOT__" = root-level users only, or a sub_org_id to view
  // one sub-organization's members only. Lets a root Admin (or Super Admin
  // viewing an org) separate root-org users from sub-org users instead of
  // always seeing them combined.
  const [orgTabFilter, setOrgTabFilter] = useState("");
  const [orgTabSubOrgs, setOrgTabSubOrgs] = useState([]);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false); // Manage dialog visibility
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState({
    timezone: "Asia/Calcutta",
    timesheet_timezone: "Asia/Calcutta",
  });
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState([]);
  const dispatch = useDispatch();

  // Get global location filter from Redux
  const globalLocationFilter = useSelector((state) => state.selectedLocationFilter);
  // Get Super Admin selected organization from Redux
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  // Full selected node (org or sub_org). When the Super Admin drilled into a
  // sub-org, this carries type:'sub_org' + sub_org_id so we can list only that
  // sub-org's members.
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  // Get user role from Redux
  const loginResponse = useSelector((state) => state.loginResponse);
  const isSuperAdmin = loginResponse?.role_name?.toUpperCase() === 'SUPER_ADMIN' || loginResponse?.roleName?.toUpperCase() === 'SUPER_ADMIN';
  const isAdmin = loginResponse?.role_name?.toUpperCase() === 'ADMIN' || loginResponse?.roleName?.toUpperCase() === 'ADMIN';
  const [isImageEnlarged, setIsImageEnlarged] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(false);
  const [selectedWorkSchedule, setSelectedWorkSchedule] = useState(false);
  const [isSupervisedMembersOpen, setIsSupervisedMembersOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isChangeManagerMode, setIsChangeManagerMode] = useState(false);
  const [selectedMembersForChange, setSelectedMembersForChange] = useState([]);
  const [newManagerId, setNewManagerId] = useState("");
  const [isAssignManagerMode, setIsAssignManagerMode] = useState(false);
  const [selectedMembersToAssign, setSelectedMembersToAssign] = useState([]);
  const [unassignedUsers, setUnassignedUsers] = useState([]);
  const [isLoadingUnassigned, setIsLoadingUnassigned] = useState(false);
  // "Change organization" (Super Admin only): revoke a user's access to their
  // current organization and grant it to another - e.g. moving a user to a
  // different Admin organization while the original Admin is on leave.
  const [isChangeOrgOpen, setIsChangeOrgOpen] = useState(false);
  const [changeOrgMember, setChangeOrgMember] = useState(null);
  const [accessibleOrgsForChange, setAccessibleOrgsForChange] = useState([]);
  const [changeOrgTargetId, setChangeOrgTargetId] = useState("");
  const [changeOrgReason, setChangeOrgReason] = useState("");
  const [isChangeOrgSaving, setIsChangeOrgSaving] = useState(false);
  const [editValues, setEditValues] = useState({
    full_name: "",
    roleName: "",
    email: "",
    mobile: "",
    employee_id: "",
    creation_date_and_time: "",
    timezone: "Asia/Calcutta",
    timesheet_timezone: "Asia/Calcutta",
  });

  const handleAddMemberSuccess = () => {
    fetchMembers(); // Refresh member list after a new member is added
    setIsAddMemberOpen(false); // Close the dialog after success
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Apply status filter from navigation state if provided
  useEffect(() => {
    if (location.state?.statusFilter) {
      setStatusFilter(location.state.statusFilter);
    }
  }, [location.state?.statusFilter]);

  // Initial fetch on mount
  useEffect(() => {
    fetchMembers();
    FetchLocation();
    loadOrgTabSubOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload the Organization filter's sub-org options when the Global Filter
  // drilldown changes scope (e.g. Super Admin switches org), and reset the
  // page-local filter back to "All" so it doesn't silently keep filtering by
  // a sub_org_id that may not exist under the newly selected org.
  useEffect(() => {
    loadOrgTabSubOrgs();
    setOrgTabFilter("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superAdminSelectedOrgId, superAdminSelectedOrgData]);

  const loadOrgTabSubOrgs = async () => {
    try {
      // Super Admin: scope to the org they've selected (or their own org if
      // none selected, matching the existing fetchSubOrgDropdown convention
      // used elsewhere, e.g. AddWorkLocation.jsx). Admin: always their own
      // org via the JWT header (org_id is never valid to send for Admin -
      // the header dropdown's selected value is a sub_org_id, not an org_id).
      const response = await fetchSubOrgDropdown(isSuperAdmin ? superAdminSelectedOrgId : null);
      setOrgTabSubOrgs(
        response?.data?.businessStatusCode === 2 ? (response.data.data || []) : []
      );
    } catch (error) {
      console.error('Failed to load sub-organizations for Users filter:', error);
      setOrgTabSubOrgs([]);
    }
  };

  // Fetch when filters or pagination changes
  useEffect(() => {
    // Skip initial render - handled by the mount effect above
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    fetchMembers();
  }, [currentPage, roleFilter, identificationFilter, statusFilter, globalLocationFilter, superAdminSelectedOrgId, superAdminSelectedOrgData, orgTabFilter]);

  const FetchLocation = async () => {
    setIsLoading(true);
    setError(null);
    const payload = {
      entity_type: "location",
      pagenumber: 1,
      parameter: [
        {
          columnName: "status",
          condition: "=",
          value: "ACTIVE",
        },
      ],
    };

    // Add org_id for Super Admin viewing specific organization
    if (isSuperAdmin && superAdminSelectedOrgId) {
      payload.org_id = superAdminSelectedOrgId;
    }

    try {
      const response = await FetchLocationApi(payload); // Fetch from your API

      // Log the entire response to debug
      console.log("Location:", response);

      // Accessing the data inside the response
      const { businessStatusCode, summaryData } = response.data;

      // Check if the response contains the data as expected
      const { viewListData } = response.data; // Update this line as per your actual response structure

      // Store the location names in the state
      setLocations(viewListData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  const fetchMembers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Build parameters array for filtering
      const parameters = [];
      
      if (roleFilter) {
        parameters.push({
          columnName: "role_name",
          condition: "=",
          value: roleFilter
        });
      }
      
      if (identificationFilter) {
        const isAuthorised = identificationFilter === "verified";
        parameters.push({
          columnName: "is_authorised",
          condition: "=",
          value: isAuthorised
        });
      }

       if (statusFilter) {
        if (statusFilter === "NOT_CHECKED_IN") {
          // Special handling for "Not Checked In" - users with NULL current_status
          parameters.push({
            columnName: "current_status",
            condition: "IS",
            value: "NULL"
          });
        } else {
          parameters.push({
            columnName: "current_status",
            condition: "=",
            value: statusFilter
          });
        }
      }

      const payload = {
        pageNumber: currentPage,
        pageSize: pageSize
      };

      // Only add parameters if there are filters
      if (parameters.length > 0) {
        payload.parameter = parameters;
      }

      // Add global location filter if selected
      if (globalLocationFilter) {
        payload.location_id = globalLocationFilter;
      }

      // Add org_id for Super Admin viewing specific organization
      if (isSuperAdmin && superAdminSelectedOrgId) {
        payload.org_id = superAdminSelectedOrgId;
        // If the Super Admin drilled into a specific SUB-ORG, scope the list to
        // that sub-org's members only (backend excludes admins/other users).
        if (superAdminSelectedOrgData?.type === 'sub_org' && superAdminSelectedOrgData?.sub_org_id) {
          payload.sub_org_id = superAdminSelectedOrgData.sub_org_id;
        }
      } else if (isAdmin && superAdminSelectedOrgData?.type === 'sub_org' && superAdminSelectedOrgData?.sub_org_id) {
        // Admin drilled into a sub-org via the header dropdown. No org_id
        // override needed/valid for Admin (their own org is implicit via the
        // JWT header) - only sub_org_id, validated server-side against the
        // caller's own accessible scope.
        payload.sub_org_id = superAdminSelectedOrgData.sub_org_id;
      } else if (orgTabFilter === "__ROOT__") {
        // Page-local Organization filter, only applies when the header's
        // Global Filter hasn't already drilled into a specific org/sub-org
        // (that takes precedence above) - shows root-level users only,
        // validated server-side against the caller's own root-admin scope.
        payload.root_only = true;
      } else if (orgTabFilter) {
        // Page-local filter to a specific sub-org, validated server-side the
        // same way as the header dropdown's drilldown above.
        payload.sub_org_id = orgTabFilter;
      }

      const response = await fetchMemberApi(payload);
      console.log("API Response:", response);
      let org_id = null;
      if (Array.isArray(response.data?.summaryData) && response.data.summaryData.length > 0) {
        org_id = response.data.summaryData[0].org_id;
        dispatch(orgid(org_id));
      }
      const { businessStatusCode, summaryData, totalPages, totalRecords } = response.data;
      if (businessStatusCode === 2 && summaryData) {
        setMembers(summaryData);
        setTotalPages(totalPages || 1);
        setTotalRecords(totalRecords || summaryData.length);
      } else {
        setMembers([]);
        setTotalPages(1);
        setTotalRecords(0);
        setError("No members found. Please add members to get started.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Client-side search filter (for search term only, not role/identification)
  const filteredMembers = members?.filter(
    (member) => {
      // Search filter only
      const matchesSearch = 
        member?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        member?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        member?.employee_id?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      
      return matchesSearch;
    }
  );

  // Get supervised members for a specific manager
  const getSupervisedMembers = (managerId) => {
    return members?.filter(member => member.supervisor_id === managerId);
  };

  // Open supervised members popup
  const handleShowSupervisedMembers = (manager, e) => {
    e.stopPropagation(); // Prevent row click
    // Instead of opening dialog, navigate to MemberDetails page with manager info
navigate("/home/member-details", { state: { selectedManager: manager, allMembers: members } });  };

  // Toggle change manager mode
  const handleChangeManagerClick = () => {
    setIsChangeManagerMode(!isChangeManagerMode);
    setSelectedMembersForChange([]);
    setNewManagerId("");
  };

  // Handle checkbox selection for individual members
  const handleMemberCheckboxChange = (memberId) => {
    setSelectedMembersForChange((prev) => {
      if (prev.includes(memberId)) {
        return prev.filter((id) => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  // Handle select all checkbox
  const handleSelectAllChange = () => {
    if (selectedManager) {
      const supervisedMembers = getSupervisedMembers(selectedManager.user_id);
      const allIds = supervisedMembers.map((m) => m.user_id);
      
      if (selectedMembersForChange.length === allIds.length) {
        setSelectedMembersForChange([]);
      } else {
        setSelectedMembersForChange(allIds);
      }
    }
  };

  // Handle applying manager change
  const handleApplyManagerChange = async () => {
    if (selectedMembersForChange.length === 0) {
      toast.warning("Please select at least one member");
      return;
    }
    if (!newManagerId) {
      toast.warning("Please select a new manager");
      return;
    }
    
    try {
      const payload = {
        managerId: newManagerId,
        memberId: selectedMembersForChange
      };
      
      console.log("Changing manager with payload:", payload);
      
      const response = await changeManagerAPI(payload);
      
      if (response.businessStatusCode === 2) {
        toast.success(response.message || "Manager changed successfully");
        setIsChangeManagerMode(false);
        setSelectedMembersForChange([]);
        setNewManagerId("");
        setIsSupervisedMembersOpen(false);
        fetchMembers(); // Refresh the member list
      } else {
        toast.error(response.message || "Failed to change manager");
      }
    } catch (error) {
      console.error("Error changing manager:", error);
      toast.error("Failed to change manager");
    }
  };

  // Cancel change manager mode
  const handleCancelManagerChange = () => {
    setIsChangeManagerMode(false);
    setSelectedMembersForChange([]);
    setNewManagerId("");
  };

  // Toggle assign manager mode
  const handleAssignManagerClick = async () => {
    const newMode = !isAssignManagerMode;
    setIsAssignManagerMode(newMode);
    setSelectedMembersToAssign([]);
    
    // Fetch unassigned users when opening assign mode
    if (newMode) {
      setIsLoadingUnassigned(true);
      try {
        const response = await fetchUnassignedUsersAPI(isSuperAdmin ? superAdminSelectedOrgId : null);
        if (response.businessStatusCode === 2) {
          setUnassignedUsers(response.data || []);
        } else {
          toast.error("Failed to fetch unassigned users");
          setUnassignedUsers([]);
        }
      } catch (error) {
        console.error("Error fetching unassigned users:", error);
        toast.error("Failed to fetch unassigned users");
        setUnassignedUsers([]);
      } finally {
        setIsLoadingUnassigned(false);
      }
    }
  };

  // Handle checkbox selection for members to assign
  const handleAssignMemberCheckboxChange = (memberId) => {
    setSelectedMembersToAssign((prev) => {
      if (prev.includes(memberId)) {
        return prev.filter((id) => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  // Get unmanaged members from API response
  const getUnmanagedMembers = () => {
    return unassignedUsers || [];
  };

  // Handle assigning members to manager
  const handleApplyAssignManager = async () => {
    if (selectedMembersToAssign.length === 0) {
      toast.warning("Please select at least one member to assign");
      return;
    }
    if (!selectedManager) {
      toast.warning("No manager selected");
      return;
    }
    
    try {
      const payload = {
        userId: selectedManager.user_id,
        memberIds: selectedMembersToAssign
      };
      
      console.log("Assigning members with payload:", payload);
      
      const response = await assignMembersAPI(payload);
      
      if (response.businessStatusCode === 2) {
        toast.success(response.message || "Members assigned successfully");
        setIsAssignManagerMode(false);
        setSelectedMembersToAssign([]);
        setUnassignedUsers([]);
        setIsSupervisedMembersOpen(false);
        fetchMembers(); // Refresh the member list
      } else {
        toast.error(response.message || "Failed to assign members");
      }
    } catch (error) {
      console.error("Error assigning members:", error);
      toast.error("Failed to assign members");
    }
  };

  // Cancel assign manager mode
  const handleCancelAssignManager = () => {
    setIsAssignManagerMode(false);
    setSelectedMembersToAssign([]);
    setUnassignedUsers([]);
  };

  // Get all managers for dropdown
  const getManagersList = () => {
    return members?.filter((member) => member.role_name === "MANAGER") || [];
  };

  // Redirect to member-details page on row click (no user_id in URL)
  const handleRowClick = (member) => {
    // See handleEditIconClick for why viewedOrgId/viewedSubOrgId must be
    // carried along — without it, Supervised Members falls back to the
    // manager's own sub_org_id instead of the context they were clicked from.
    navigate(`/home/member-details`, {
      state: {
        selectedMember: member,
        allMembers: members,
        viewedOrgId: isSuperAdmin ? superAdminSelectedOrgId : null,
        viewedSubOrgId: (isSuperAdmin || isAdmin) && superAdminSelectedOrgData?.type === 'sub_org'
          ? superAdminSelectedOrgData?.sub_org_id
          : null,
      }
    });
  };

  const handleImageClick = () => {
    setIsImageEnlarged(true);
  };

  const handleCloseImage = () => {
    setIsImageEnlarged(false);
  };

  const handleEditClick = () => {
    const formattedDate = selectedMember?.creation_date_and_time 
      ? new Date(selectedMember.creation_date_and_time).toISOString().split('T')[0]
      : '';

    setEditValues({
      full_name: selectedMember?.full_name || "",
      roleName: selectedMember?.role_name || "",
      email: selectedMember?.email || "",
      mobile: selectedMember?.mobile || "",
      employee_id: selectedMember?.employee_id || "",
      creation_date_and_time: formattedDate,
      timezone: "Asia/Calcutta" || "",
      timesheet_timezone: "Asia/Calcutta" || "",
    });
    setIsEditing(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSaveClick = async () => {
    try {
      const payload = {
        entity_type: "user",
        comment: "Profile updated",
        old_data: {
          full_name: selectedMember?.full_name || "",
          email: selectedMember?.email || "",
          mobile: selectedMember?.mobile || "",
          employee_id: selectedMember?.employee_id || "",
          creation_date_and_time: selectedMember?.creation_date_and_time || "",
          role_name: selectedMember?.role_name || "",
          user_id: selectedMember?.user_id || "",
          org_id: selectedMember?.org_id || "",
          role_id: selectedMember?.role_id || "",
        },
        new_data: {
          full_name: editValues.full_name,
          email: editValues.email,
          mobile: editValues.mobile,
          employee_id: editValues.employee_id,
          creation_date_and_time: editValues.creation_date_and_time,
          role_name: editValues.roleName,
          user_id: selectedMember.user_id,
          org_id: selectedMember.org_id,
          role_id: selectedMember.role_id,
        }
      };

      const result = await updateMemberAPI(payload);
      toast.success(result.message);
      setSelectedMember(prevMember => ({
        ...prevMember,
        ...payload.new_data
      }));
      setIsEditing(false);
      fetchMembers();
    } catch (error) {
      console.error('Error updating member:', error);
    }
  };
  

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditValues({
      timezone: selectedMember.timezone,
      timesheet_timezone: selectedMember.timesheet_timezone,
    });
  };

  const handleEditIconClick = (member, e) => {
    e.stopPropagation(); // Prevent row click
    // Carry the Super Admin's current org/sub-org selection along so the
    // Member Details page's "Supervised Members" list is scoped to the SAME
    // context the manager was clicked from (root org vs a specific sub-org) —
    // otherwise a manager assigned to a sub-org always shows only that
    // sub-org's reports, even when viewed from the root org tab.
    navigate("/home/member-details", {
      state: {
        selectedMember: member,
        viewedOrgId: isSuperAdmin ? superAdminSelectedOrgId : null,
        viewedSubOrgId: (isSuperAdmin || isAdmin) && superAdminSelectedOrgData?.type === 'sub_org'
          ? superAdminSelectedOrgData?.sub_org_id
          : null,
      },
    });
  };

  const handleDeleteIconClick = (member, e) => {
    e.stopPropagation(); // Prevent row click
    if (window.confirm(`Are you sure you want to delete ${member.full_name || member.email}?`)) {
      inActiveUser({ userId: member.user_id })
        .then((result) => {
          toast.success(result.message || "Member deleted successfully");
          fetchMembers();
        })
        .catch((error) => {
          toast.error("Failed to delete member");
        });
    }
  };

  const handleChangeOrgIconClick = async (member, e) => {
    e.stopPropagation(); // Prevent row click
    setChangeOrgMember(member);
    setChangeOrgTargetId("");
    setChangeOrgReason("");
    setIsChangeOrgOpen(true);
    try {
      const res = await fetchAccessibleOrgsAPI();
      setAccessibleOrgsForChange(res?.businessStatusCode === 2 ? (res.data || []) : []);
    } catch (error) {
      console.error("Failed to fetch accessible organizations:", error);
      setAccessibleOrgsForChange([]);
    }
  };

  const handleChangeOrgConfirm = async () => {
    if (!changeOrgMember || !changeOrgTargetId) return;
    setIsChangeOrgSaving(true);
    try {
      const result = await changeUserOrganization(
        changeOrgMember.user_id,
        changeOrgTargetId,
        changeOrgReason.trim()
      );
      if (result?.businessStatusCode !== 2) {
        throw new Error(result?.message || "Failed to change organization");
      }
      toast.success(result?.message || "Organization access updated successfully");
      setIsChangeOrgOpen(false);
      fetchMembers();
    } catch (error) {
      toast.error("Failed to change organization: " + (error.response?.data?.message || error.message));
    } finally {
      setIsChangeOrgSaving(false);
    }
  };

  const handleLocationChange = (selectedOptions) => {
    setSelectedLocationId(selectedOptions.map((option) => option.value));
  };

  const locationOptions = locations?.map((location) => ({
    value: location.location_id,
    label: location.location_name,
    radius: location.radius,
  }));

  // Show message for Super Admin to select an organization
  if (isSuperAdmin && !superAdminSelectedOrgId) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen" style={{ backgroundColor: "#EFF0F4" }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Users</h1>
            <p className="text-sm text-gray-1000">Super Admin View</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center bg-white rounded-lg shadow-sm p-12 mt-8">
          <div className="text-amber-1000 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Select an Organization</h2>
          <p className="text-gray-1000 text-center max-w-md">
            Please select an organization from the dropdown in the header to view its users.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container-fluid px-3 m-0">
        <div
          className="row pb-1 px-3  "
          style={{ minHeight: "10vh" }}
        >
        {/* Search Bar and Filters */}
        <div className="col-12 col-md-12 m-0 p-0 mt-3 mb-2">
          <div className="bg-white rounded-lg shadow-sm py-2 px-2 px-md-3">
            {/* Mobile: Stack vertically, Desktop: Flex row */}
            <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2">
              {/* Search Input - Full width on mobile */}
              <div className="flex-grow-1 order-1" style={{ minWidth: '0' }}>
                <div className="position-relative">
                  <Search
                    className="position-absolute text-gray-400"
                    style={{
                      left: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '16px',
                      height: '16px',
                      pointerEvents: 'none'
                    }}
                  />
                  <input
                    type="text"
                    className="form-control ps-4"
                    style={{
                      height: '38px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '13px',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#ffffff'
                    }}
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FF8C00';
                      e.target.style.boxShadow = '0 0 0 2px rgba(255, 140, 0, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e0e0e0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Filters Section - Row on mobile with smaller selects */}
              <div className="d-flex align-items-center gap-2 order-2">
                {/* Role Filter */}
                <div className="flex-grow-1 flex-md-grow-0" style={{ minWidth: '100px' }}>
                  <select
                    className="form-select"
                    style={{
                      height: '38px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '12px',
                      paddingLeft: '8px',
                      paddingRight: '28px',
                      backgroundColor: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontWeight: roleFilter ? '1000' : '400',
                      color: roleFilter ? '#1f2937' : '#6b7280'
                    }}
                    value={roleFilter}
                    onChange={(e) => {
                      setRoleFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FF8C00';
                      e.target.style.boxShadow = '0 0 0 2px rgba(255, 140, 0, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e0e0e0';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">All Roles</option>
                    <option value="MANAGER">Manager</option>
                    <option value="MEMBER">Member</option>
                  </select>
                </div>

                {/* Identification Filter */}
                <div className="flex-grow-1 flex-md-grow-0 d-none d-sm-block" style={{ minWidth: '130px' }}>
                  <select
                    className="form-select"
                    style={{
                      height: '38px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '12px',
                      paddingLeft: '8px',
                      paddingRight: '28px',
                      backgroundColor: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontWeight: identificationFilter ? '1000' : '400',
                      color: identificationFilter ? '#1f2937' : '#6b7280'
                    }}
                    value={identificationFilter}
                    onChange={(e) => {
                      setIdentificationFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FF8C00';
                      e.target.style.boxShadow = '0 0 0 2px rgba(255, 140, 0, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e0e0e0';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">All Status</option>
                    <option value="verified">Verified</option>
                    <option value="unverified">Unverified</option>
                  </select>
                </div>

                 <div style={{ minWidth: '160px' }}>
                  <select
                    className="form-select"
                    style={{
                      height: '38px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      paddingLeft: '10px',
                      paddingRight: '32px',
                      backgroundColor: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontWeight: statusFilter ? '1000' : '400',
                      color: statusFilter ? '#1f2937' : '#6b7280'
                    }}
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1); // Reset to first page when filter changes
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#FF8C00';
                      e.target.style.boxShadow = '0 0 0 2px rgba(255, 140, 0, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e0e0e0';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">All </option>
                    <option value="CHECKIN">Checked In</option>
                    <option value="BREAK">On Break</option>
                    <option value="CHECKOUT">Checked Out</option>
                    <option value="NOT_CHECKED_IN">Not Checked In</option>
                  </select>
                </div>

                {/* Organization Filter: only shown when there are sub-orgs to
                    split by, and only when the header's Global Filter hasn't
                    already drilled into one specific org/sub-org (that takes
                    precedence and this filter would be redundant/confusing
                    alongside it). Lets root-level and sub-org users be viewed
                    as separate buckets instead of always mixed together. */}
                {orgTabSubOrgs.length > 0 && !(superAdminSelectedOrgData?.type === 'sub_org' && superAdminSelectedOrgData?.sub_org_id) && (
                  <div className="flex-grow-1 flex-md-grow-0" style={{ minWidth: '160px' }}>
                    <select
                      className="form-select"
                      style={{
                        height: '38px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '12px',
                        paddingLeft: '8px',
                        paddingRight: '28px',
                        backgroundColor: '#ffffff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontWeight: orgTabFilter ? '1000' : '400',
                        color: orgTabFilter ? '#1f2937' : '#6b7280'
                      }}
                      value={orgTabFilter}
                      onChange={(e) => {
                        setOrgTabFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#FF8C00';
                        e.target.style.boxShadow = '0 0 0 2px rgba(255, 140, 0, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e0e0e0';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="">All Organizations</option>
                      <option value="__ROOT__">Root Organization</option>
                      {orgTabSubOrgs.map((subOrg) => (
                        <option key={subOrg.sub_org_id} value={subOrg.sub_org_id}>
                          {subOrg.sub_org_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

                {/* Add User Button - Disabled when org is suspended */}
                <ArthaContinueButton
                  style={{
                    height: '38px',
                    padding: '0 12px',
                    cursor: isSuspended ? "not-allowed" : "pointer",
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '1000',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    opacity: isSuspended ? 0.5 : 1,
                  }}
                  onClick={() => !isSuspended && setIsAddMemberOpen(true)}
                  disabled={isSuspended}
                  title={isSuspended ? "Actions disabled - Organization is suspended" : "Add a new user"}
                >
                  {isSuspended ? <Lock size={14} /> : null}
                  + Add User
                </ArthaContinueButton>
              </div>
            </div>
          </div>
        </div>

        {/* Members Table - Only this section scrolls */}
        <div className="col-12 col-md-12 m-0 p-0" >
          <div
            className="col-12 mt-0 bg-white shadow-sm rounded-lg m-0 p-0"
            style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)' }}
          >
            {/* Table Header (fixed) - Hidden on mobile */}
            <div
              className="members-table-header items-center px-4 py-3 border-b"
              style={{
                backgroundColor: '#f8f9fa',
                position: 'sticky',
                top: 0,
                zIndex: 2,
              }}
            >
              <div className="flex items-center">
                <span
                  className="ml-3 text-sm text-gray-600 fw-bold"
                  style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '11px' }}
                >
                  Member Name
                </span>
              </div>
              <div className="text-sm text-gray-600 fw-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '11px' }}>
                Email
              </div>
              <div className="text-sm text-gray-600 fw-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '11px' }}>
                Role
              </div>
              <div className="text-sm text-gray-600 fw-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '11px' }}>
                Employment Type
              </div>
              <div className="text-sm text-gray-600 fw-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '11px' }}>
                Status
              </div>
              <div className="text-sm text-gray-600 fw-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '11px' }}>
                Verification
              </div>
              <div className="text-sm text-gray-600 fw-bold" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '11px' }}>
                Actions
              </div>
            </div>
            {/* Table Body (scrollable) */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {isLoading ? (
                <div className="col-12 text-center mt-2 py-4">Loading...</div>
              ) : error ? (
                <div className="col-12 text-center mt-2 text-red-1000 py-4">{error}</div>
              ) : (
                filteredMembers.map((member) => {
                  const isManager = member.role_name === "MANAGER";
                  return (
                    <React.Fragment key={member.user_id}>
                      {/* Desktop Table Row - Hidden on mobile */}
                      <div
                        className="members-table-row items-center ps-1 pe-4 py-2 border-b hover:bg-gray-50"
                        onClick={() => handleRowClick(member)}
                        style={{ cursor: 'pointer', alignItems: 'start' }}
                      >
                        <div className="flex items-start pt-1">
                          <div className="ml-3 flex items-start">
                            <UserAvatar member={member} />
                            <div className="ml-3" style={{ minWidth: 0, flex: 1 }}>
                              <div
                                className="text-sm font-medium text-gray-900"
                                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.5', fontSize: '12px' }}
                                title={
                                  member.full_name && member.full_name.trim() !== "" && member.full_name !== "null"
                                    ? member.full_name
                                    : member.email || member.user_id
                                }
                              >
                                {member.full_name && member.full_name.trim() !== "" && member.full_name !== "null"
                                  ? member.full_name
                                  : member.email || member.user_id}
                              </div>
                              {isManager && (member.supervised_member_count || 0) > 0 && (
                                <div className="text-xs text-gray-1000 mt-1" style={{ lineHeight: '1.3', fontSize: '10px' }}>
                                  Supervises {member.supervised_member_count || 0} member
                                </div>
                              )}
                              {isManager && (
                                <div className="text-xs text-gray-1000 mt-1" style={{ lineHeight: '1.3', fontSize: '10px' }}>
                                  Manages {member.assigned_sub_org_count || 0} sub-org{(member.assigned_sub_org_count || 0) === 1 ? '' : 's'}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div
                          className="text-sm text-gray-900 pt-1"
                          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.5', fontSize: '11px' }}
                          title={member.email}
                        >
                          {member.email}
                        </div>
                        <div className="text-sm text-gray-600 pt-1" style={{ lineHeight: '1.5', fontSize: '11px' }}>
                          {member.role_name}
                        </div>
                        <div className="text-sm text-gray-600 pt-1" style={{ lineHeight: '1.5', fontSize: '11px' }}>
                          {member.employment_type || '—'}
                        </div>
                        <div className="pt-1">
                          <span
                            className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                              member.status === "ACTIVE"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                            style={{ fontSize: '10px' }}
                          >
                            {member.status === "ACTIVE" ? "ACTIVE" : "INACTIVE"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 pt-1">
                          {member.is_authorised === true ? (
                            <>
                              <CheckCircle className="h-3 w-3 text-green-600" />
                              <span className="text-green-600 font-medium" style={{ fontSize: '10px' }}>Verified</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-3 w-3 text-red-600" />
                              <span className="text-red-600 font-medium" style={{ fontSize: '10px' }}>Unverified</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-1 pt-1">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isSuspended) handleEditIconClick(member, e);
                            }}
                            className="hover:bg-blue-50"
                            title={isSuspended ? "Actions disabled - Organization is suspended" : "Edit member"}
                            style={{ padding: '4px', opacity: isSuspended ? 0.5 : 1, cursor: isSuspended ? 'not-allowed' : 'pointer' }}
                            disabled={isSuspended}
                          >
                            <Edit className="h-4 w-4 text-blue-600" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isSuspended) handleDeleteIconClick(member, e);
                            }}
                            className="hover:bg-red-50"
                            title={isSuspended ? "Actions disabled - Organization is suspended" : "Delete member"}
                            style={{ padding: '4px', opacity: isSuspended ? 0.5 : 1, cursor: isSuspended ? 'not-allowed' : 'pointer' }}
                            disabled={isSuspended}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </IconButton>
                          {isSuperAdmin && superAdminSelectedOrgId && member.role_name !== 'SUPER_ADMIN' && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                if (!isSuspended) handleChangeOrgIconClick(member, e);
                              }}
                              className="hover:bg-amber-50"
                              title={isSuspended ? "Actions disabled - Organization is suspended" : "Change organization access"}
                              style={{ padding: '4px', opacity: isSuspended ? 0.5 : 1, cursor: isSuspended ? 'not-allowed' : 'pointer' }}
                              disabled={isSuspended}
                            >
                              <Repeat className="h-4 w-4 text-amber-600" />
                            </IconButton>
                          )}
                        </div>
                      </div>

                      {/* Mobile Card View - Visible only on mobile */}
                      <div
                        className="members-mobile-card border-b p-3"
                        onClick={() => handleRowClick(member)}
                        style={{ cursor: 'pointer', backgroundColor: '#fff' }}
                      >
                        <div className="d-flex align-items-start justify-content-between">
                          {/* Left: Avatar and Info */}
                          <div className="d-flex align-items-start gap-2" style={{ flex: 1, minWidth: 0 }}>
                            <UserAvatar member={member} />
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div
                                className="fw-medium text-gray-900"
                                style={{ fontSize: '13px', lineHeight: '1.4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {member.full_name && member.full_name.trim() !== "" && member.full_name !== "null"
                                  ? member.full_name
                                  : member.email || member.user_id}
                              </div>
                              <div
                                className="text-gray-1000"
                                style={{ fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {member.email}
                              </div>
                              <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                                <span
                                  className="text-gray-600"
                                  style={{ fontSize: '10px', backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}
                                >
                                  {member.role_name}
                                </span>
                                <span
                                  className={`rounded-full px-2 ${
                                    member.status === "ACTIVE"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                  style={{ fontSize: '10px' }}
                                >
                                  {member.status}
                                </span>
                                {member.is_authorised === true ? (
                                  <span className="d-flex align-items-center gap-1 text-green-600" style={{ fontSize: '10px' }}>
                                    <CheckCircle style={{ width: '12px', height: '12px' }} />
                                    Verified
                                  </span>
                                ) : (
                                  <span className="d-flex align-items-center gap-1 text-red-600" style={{ fontSize: '10px' }}>
                                    <XCircle style={{ width: '12px', height: '12px' }} />
                                    Unverified
                                  </span>
                                )}
                                {member.role_name === "MANAGER" && (
                                  <span
                                    className="text-gray-600"
                                    style={{ fontSize: '10px', backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}
                                  >
                                    Manages {member.assigned_sub_org_count || 0} sub-org{(member.assigned_sub_org_count || 0) === 1 ? '' : 's'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* Right: Action Buttons */}
                          <div className="d-flex align-items-center gap-1 ms-2">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isSuspended) handleEditIconClick(member, e);
                              }}
                              style={{ padding: '6px', opacity: isSuspended ? 0.5 : 1, cursor: isSuspended ? 'not-allowed' : 'pointer' }}
                              disabled={isSuspended}
                              title={isSuspended ? "Actions disabled - Organization is suspended" : "Edit member"}
                            >
                              <Edit style={{ width: '16px', height: '16px', color: '#2563eb' }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isSuspended) handleDeleteIconClick(member, e);
                              }}
                              style={{ padding: '6px', opacity: isSuspended ? 0.5 : 1, cursor: isSuspended ? 'not-allowed' : 'pointer' }}
                              disabled={isSuspended}
                              title={isSuspended ? "Actions disabled - Organization is suspended" : "Delete member"}
                            >
                              <Trash2 style={{ width: '16px', height: '16px', color: '#dc2626' }} />
                            </IconButton>
                            {isSuperAdmin && superAdminSelectedOrgId && member.role_name !== 'SUPER_ADMIN' && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  if (!isSuspended) handleChangeOrgIconClick(member, e);
                                }}
                                style={{ padding: '6px', opacity: isSuspended ? 0.5 : 1, cursor: isSuspended ? 'not-allowed' : 'pointer' }}
                                disabled={isSuspended}
                                title={isSuspended ? "Actions disabled - Organization is suspended" : "Change organization access"}
                              >
                                <Repeat style={{ width: '16px', height: '16px', color: '#b45309' }} />
                              </IconButton>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                    
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Supervised Members Popup Dialog */}
        <Dialog
          open={isSupervisedMembersOpen}
          onClose={() => setIsSupervisedMembersOpen(false)}
          TransitionComponent={Transition}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            }
          }}
        >
          <div className="p-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{ borderBottom: '1px solid #e5e7eb' }}>
              <div>
                <h6 className="m-0 fw-bold text-start" style={{ color: '#111827', fontSize: '16px' }}>
                  Supervised Members
                  <span className="ms-2 text-muted" style={{ fontSize: '13px', fontWeight: '400' }}>
                    ({selectedManager?.full_name || selectedManager?.email})
                  </span>
                </h6>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  onClick={handleAssignManagerClick}
                  style={{
                    height: '32px',
                    padding: '0 14px',
                    fontSize: '13px',
                    fontWeight: '1000',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: isAssignManagerMode ? '#f3f4f6' : '#28a745',
                    color: isAssignManagerMode ? '#374151' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = isAssignManagerMode ? '#e5e7eb' : '#218838';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = isAssignManagerMode ? '#f3f4f6' : '#28a745';
                  }}
                >
                  {isAssignManagerMode ? (
                    <>
                      <XCircle style={{ width: '14px', height: '14px' }} />
                      Cancel
                    </>
                  ) : (
                    <>
                      <UserPlus style={{ width: '14px', height: '14px' }} />
                      Add Team Member
                    </>
                  )}
                </button>
                <button
                  onClick={handleChangeManagerClick}
                  style={{
                    height: '32px',
                    padding: '0 14px',
                    fontSize: '13px',
                    fontWeight: '1000',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: isChangeManagerMode ? '#f3f4f6' : '#FF8C00',
                    color: isChangeManagerMode ? '#374151' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = isChangeManagerMode ? '#e5e7eb' : '#e67700';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = isChangeManagerMode ? '#f3f4f6' : '#FF8C00';
                  }}
                >
                  {isChangeManagerMode ? (
                    <>
                      <XCircle style={{ width: '14px', height: '14px' }} />
                      Cancel
                    </>
                  ) : (
                    <>
                      <Edit style={{ width: '14px', height: '14px' }} />
                      Change Manager
                    </>
                  )}
                </button>
                <button
                  className="btn-close"
                  onClick={() => setIsSupervisedMembersOpen(false)}
                  style={{
                    fontSize: '12px',
                    padding: '8px',
                  }}
                />
              </div>
            </div>

            {/* Change Manager Section */}
            {isChangeManagerMode && (
              <div 
                className="mb-3 p-3 rounded" 
                style={{ 
                  backgroundColor: '#fef3e7',
                  border: '1px solid #FFB84D',
                }}
              >
                <div className="row g-2">
                  <div className="col-md-7">
                    <div className="position-relative">
                      <select
                        className="form-select form-select-sm"
                        value={newManagerId}
                        onChange={(e) => setNewManagerId(e.target.value)}
                        style={{
                          fontSize: '13px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          padding: '6px 32px 6px 10px',
                          backgroundColor: 'white',
                          color: '#374151',
                          fontWeight: '1000',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          height: '36px',
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#FF8C00';
                          e.target.style.outline = 'none';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                        }}
                      >
                        <option value="">Select new manager...</option>
                        {getManagersList()
                          .filter((manager) => manager.user_id !== selectedManager?.user_id)
                          .map((manager) => (
                            <option key={manager.user_id} value={manager.user_id}>
                              {manager.full_name || manager.email}
                            </option>
                          ))}
                      </select>
                      {newManagerId && (
                        <CheckCircle 
                          className="position-absolute" 
                          style={{ 
                            right: '10px', 
                            top: '50%', 
                            transform: 'translateY(-50%)',
                            width: '16px',
                            height: '16px',
                            color: '#10b981',
                            pointerEvents: 'none',
                          }} 
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-md-5">
                    <button
                      onClick={handleApplyManagerChange}
                      disabled={selectedMembersForChange.length === 0 || !newManagerId}
                      style={{
                        height: '36px',
                        padding: '0 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: (selectedMembersForChange.length === 0 || !newManagerId) ? '#d1d5db' : '#10b981',
                        color: 'white',
                        cursor: (selectedMembersForChange.length === 0 || !newManagerId) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        opacity: (selectedMembersForChange.length === 0 || !newManagerId) ? '0.6' : '1',
                      }}
                      onMouseEnter={(e) => {
                        if (selectedMembersForChange.length > 0 && newManagerId) {
                          e.target.style.backgroundColor = '#059669';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedMembersForChange.length > 0 && newManagerId) {
                          e.target.style.backgroundColor = '#10b981';
                        }
                      }}
                    >
                      <CheckCircle style={{ width: '14px', height: '14px' }} />
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Assign Manager Section */}
            {isAssignManagerMode && (
              <div 
                className="mb-3 p-3 rounded" 
                style={{ 
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #4caf50',
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UserPlus size={16} />
                    Assign Members to {selectedManager?.full_name || selectedManager?.email}
                  </h6>
                  <button
                    onClick={handleApplyAssignManager}
                    disabled={selectedMembersToAssign.length === 0}
                    style={{
                      width: 'auto',
                      height: '32px',
                      padding: '0 16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: selectedMembersToAssign.length === 0 ? '#d1d5db' : '#10b981',
                      color: 'white',
                      cursor: selectedMembersToAssign.length === 0 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      opacity: selectedMembersToAssign.length === 0 ? '0.6' : '1',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedMembersToAssign.length > 0) {
                        e.target.style.backgroundColor = '#059669';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedMembersToAssign.length > 0) {
                        e.target.style.backgroundColor = '#10b981';
                      }
                    }}
                  >
                    <CheckCircle style={{ width: '14px', height: '14px' }} />
                    Assign Members
                  </button>
                </div>
                <div className="mb-3">
                  <div style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                  }}>
                    {isLoadingUnassigned ? (
                      <div className="p-3 text-center text-muted" style={{ fontSize: '13px' }}>
                        Loading unassigned members...
                      </div>
                    ) : getUnmanagedMembers().length === 0 ? (
                      <div className="p-3 text-center text-muted" style={{ fontSize: '13px' }}>
                        No unassigned members available
                      </div>
                    ) : (
                      getUnmanagedMembers().map((member) => (
                        <div
                          key={member.user_id}
                          className="p-2 border-bottom d-flex align-items-center"
                          style={{
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                          onClick={() => handleAssignMemberCheckboxChange(member.user_id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedMembersToAssign.includes(member.user_id)}
                            onChange={() => handleAssignMemberCheckboxChange(member.user_id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer',
                              marginRight: '12px',
                            }}
                          />
                          <div className="flex-grow-1">
                            <div style={{ fontSize: '13px', fontWeight: '1000', color: '#374151' }}>
                              <span style={{ color: '#059669', marginRight: '8px' }}>{member.role_name || 'N/A'}</span>
                              {member.full_name || member.email}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>
                              {member.email}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                {selectedMembersToAssign.length > 0 && (
                  <div className="mb-2 p-2" style={{ 
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #3b82f6',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#1e40af',
                  }}>
                    {selectedMembersToAssign.length} member{selectedMembersToAssign.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
            )}
          </div>
        </Dialog>

        <Dialog
          open={isAddMemberOpen}
          onClose={() => setIsAddMemberOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <AddMember
            isOpen={isAddMemberOpen}
            onClose={() => setIsAddMemberOpen(false)}
            onSuccess={handleAddMemberSuccess}
          />
        </Dialog>

        <Dialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          TransitionComponent={Transition}
          maxWidth="sm"
          fullWidth
        >
          <div className="profile-dialog p-4">
            {/* Header */}
            <div className="dialog-header d-flex justify-content-between align-items-center mb-3">
              <h5 className="m-0 fw-bold text-start">{isEditing ? "Edit Profile" : "View Profile"}</h5>
              <button
                className="btn-close"
                onClick={() => setIsDialogOpen(false)}
              />
            </div>

            {/* User Info Section */}
            <div className="user-section d-flex gap-3 mb-4 pb-3 border-bottom">
              {/* Image section - 1/4 width */}
              <div className="profile-image-section" style={{ width: '25%' }}>
                <div className="position-relative">
                  <span onClick={handleImageClick} style={{ cursor: "pointer" }}>
                    <UserAvatar1 selectedMember={selectedMember} />
                    <span
                      className={`status-indicator position-absolute top-0 end-0 translate-middle rounded-circle ${
                        selectedMember?.current_status === "CHECKIN" ? "bg-success" : "bg-danger"
                      }`}
                      style={{ width: "8px", height: "8px", border: "2px solid white" }}
                    />
                  </span>
                </div>
              </div>
              
              {/* Form section - 3/4 width */}
              <div className="flex-grow-1" style={{ width: '75%' }}>
                {isEditing ? (
                  <div className="edit-form">
                    <div className="row g-1">
                      <div className="col-12">
                        <div className="d-flex align-items-center mb-1">
                          <label className="text-muted me-2 w-25">Full Name:</label>
                          <ArthaTextField
                            name="full_name"
                            value={editValues.full_name}
                            onChange={handleInputChange}
                            size="small"
                            fullWidth
                            className="text-start"
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="d-flex align-items-center">
                          <label className="text-muted me-2 w-25">Role:</label>
                          <ArthaTextField
                            name="roleName"
                            value={editValues.roleName}
                            onChange={handleInputChange}
                            size="small"
                            fullWidth
                            className="text-start"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex align-items-start gap-2">
                    <div className="text-start">
                      <h6 className="mb-1 fw-bold">
                        {selectedMember?.full_name && selectedMember?.full_name.trim() !== "" && selectedMember?.full_name !== "null"
                          ? selectedMember?.full_name
                          : selectedMember?.email || selectedMember?.user_id}
                      </h6>
                      <small className="text-muted d-block">{selectedMember?.role_name}</small>
                    </div>
                    <IconButton
                      size="small"
                      onClick={isSuspended ? undefined : handleEditClick}
                      className="ms-auto"
                      disabled={isSuspended}
                      title={isSuspended ? "Editing disabled - Organization is suspended" : "Edit member"}
                      style={isSuspended ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                      {isSuspended ? <Lock className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                    </IconButton>
                  </div>
                )}
              </div>
            </div>

            {/* Contact & Employment Info */}
            <div className="info-sections">
              {/* Contact Section */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-start">Contact Information</h6>
                {isEditing ? (
                  <div className="row g-2">
                    <div className="col-12">
                      <div className="d-flex align-items-center mb-2">
                        <label className="text-muted me-3 w-25">Email:</label>
                        <ArthaTextField
                          name="email"
                          value={editValues.email}
                          onChange={handleInputChange}
                          size="small"
                          fullWidth
                          className="text-start"
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-center">
                        <label className="text-muted me-3 w-25">Phone:</label>
                        <ArthaTextField
                          name="mobile"
                          value={editValues.mobile}
                          onChange={handleInputChange}
                          size="small"
                          fullWidth
                          className="text-start"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="info-grid text-start">
                    <div className="info-row d-flex">
                      <span className="info-label">Email:</span>
                      <span className="info-value flex-grow-1">{selectedMember?.email || "N/A"}</span>
                    </div>
                    <div className="info-row d-flex">
                      <span className="info-label">Phone:</span>
                      <span className="info-value flex-grow-1">{selectedMember?.mobile || "Add phone number"}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Employment Section */}
            
              <div className="mb-3">
                <h6 className="fw-bold mb-3 text-start">Employment Details</h6>
                {isEditing ? (
                  <div className="row g-2">
                    <div className="col-12">
                      <div className="d-flex align-items-center mb-2">
                        <label className="text-muted me-3 w-25">Member Code:</label>
                        <ArthaTextField
                          name="employee_id"
                          value={editValues.employee_id}
                          onChange={handleInputChange}
                          size="small"
                          fullWidth
                          className="text-start"
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-center">
                        <label className="text-muted me-3 w-25">Join Date:</label>
                        <ArthaTextField
                          type="date"
                          name="creation_date_and_time"
                          value={editValues.creation_date_and_time}
                          onChange={handleInputChange}
                          size="small"
                          fullWidth
                          className="text-start"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="info-grid text-start">
                    <div className="info-row d-flex">
                      <span className="info-label">Member Code:</span>
                      <span className="info-value flex-grow-1">{selectedMember?.employee_id || "N/A"}</span>
                    </div>
                    <div className="info-row d-flex">
                      <span className="info-label">Join Date:</span>
                      <span className="info-value flex-grow-1">
                        {formatDate(selectedMember?.creation_date_and_time || "N/A")}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                <ArthaCancelButton
                  size="small"
                  onClick={handleCancelClick}
                >
                  Cancel
                </ArthaCancelButton>
                <ArthaContinueButton
                  size="small"
                  onClick={isSuspended ? undefined : handleSaveClick}
                  disabled={isSuspended}
                  style={isSuspended ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  title={isSuspended ? "Saving disabled - Organization is suspended" : ""}
                >
                  {isSuspended && <Lock size={14} style={{ marginRight: 4 }} />}
                  Save Changes
                </ArthaContinueButton>
              </div>
            )}
          </div>
        </Dialog>

        <Dialog
          open={isImageEnlarged}
          onClose={handleCloseImage}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-center items-center p-4 overflow-hidden">
            <img
              src={`data:image/jpeg;base64,${selectedMember?.user_image}`}
              alt={selectedMember?.full_name}
              className="max-w-full max-h-full object-cover"
            />
          </div>
        </Dialog>

        {/* Change Organization Dialog (Super Admin only) */}
        <Dialog
          open={isChangeOrgOpen}
          onClose={() => !isChangeOrgSaving && setIsChangeOrgOpen(false)}
          TransitionComponent={Transition}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            }
          }}
        >
          <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{ borderBottom: '1px solid #e5e7eb' }}>
              <h6 className="m-0 fw-bold" style={{ color: '#111827', fontSize: '16px' }}>
                Change Organization Access
              </h6>
            </div>

            <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '10px' }}>
              Revoke this user's access to their current organization and grant it to another
              (e.g. covering while the assigned Admin is on leave).
            </p>

            <div style={{ fontSize: '13px', marginTop: '10px', lineHeight: '1.6' }}>
              <div><strong>Name:</strong> {changeOrgMember?.full_name || changeOrgMember?.email}</div>
              <div><strong>Email:</strong> {changeOrgMember?.email}</div>
              <div><strong>Role:</strong> {changeOrgMember?.role_name}</div>
            </div>

            <div style={{ marginTop: '16px' }}>
              <label htmlFor="member-change-org-target" style={{ fontWeight: 600, fontSize: '13px' }}>
                Grant access to <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                id="member-change-org-target"
                className="form-select"
                value={changeOrgTargetId}
                onChange={(e) => setChangeOrgTargetId(e.target.value)}
                style={{ marginTop: '6px' }}
              >
                <option value="">Select an organization…</option>
                {accessibleOrgsForChange
                  .filter((org) => org.org_id !== changeOrgMember?.org_id)
                  .map((org) => (
                    <option key={org.org_id} value={org.org_id}>
                      {'—'.repeat(org.hierarchy_level || 0)} {org.org_name}
                    </option>
                  ))}
              </select>
              {accessibleOrgsForChange.length === 0 && (
                <div style={{ fontSize: '12px', color: '#b45309', marginTop: '6px' }}>
                  No organizations available to move this user into.
                </div>
              )}
            </div>

            <div style={{ marginTop: '14px' }}>
              <label htmlFor="member-change-org-reason" style={{ fontWeight: 600, fontSize: '13px' }}>
                Reason
              </label>
              <input
                id="member-change-org-reason"
                type="text"
                className="form-control"
                value={changeOrgReason}
                onChange={(e) => setChangeOrgReason(e.target.value)}
                placeholder="e.g. Covering while the assigned Admin is on leave"
                maxLength={255}
                style={{ marginTop: '6px' }}
              />
            </div>

            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '12px' }}>
              The user immediately loses access to their current organization's data and gains
              access to the selected one. Their role is unchanged, and any sub-organization
              assignment is cleared.
            </div>

            <div className="d-flex justify-content-end gap-2" style={{ marginTop: '18px' }}>
              <ArthaCancelButton
                onClick={() => setIsChangeOrgOpen(false)}
                disabled={isChangeOrgSaving}
              >
                Cancel
              </ArthaCancelButton>
              <ArthaContinueButton
                onClick={handleChangeOrgConfirm}
                disabled={isChangeOrgSaving || !changeOrgTargetId}
              >
                {isChangeOrgSaving ? 'Working…' : 'Confirm'}
              </ArthaContinueButton>
            </div>
          </div>
        </Dialog>

        {/* Pagination Controls */}
        <div className="d-flex align-items-center justify-content-between py-2 px-2 px-md-4" style={{ background: '#fff', borderRadius: '0 0 8px 8px', marginTop: '8px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ fontSize: '13px', color: '#555' }}>
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalRecords)} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} results
          </div>
          <div className="d-flex align-items-center" style={{ gap: '6px' }}>
            <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '12px', padding: '3px 10px' }} onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
            <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '12px', padding: '3px 10px' }} onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>&lt;</button>
            <span style={{ fontSize: '13px', fontWeight: 600, padding: '3px 10px' }}>Page {currentPage} of {totalPages}</span>
            <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '12px', padding: '3px 10px' }} onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>&gt;</button>
            <button className="btn btn-sm" style={{ fontSize: '12px', padding: '3px 10px', backgroundColor: '#FF8800', color: '#fff', border: 'none' }} onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Members;
